import React, { useState } from 'react';
import { Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline category picker with built-in "+ Ajouter" creation.
 * Used by both Products quick-add form and ProductCreate validate phase.
 */
const PRESET_COLORS = [
  { color: '#EA580C', bg: '#FFF7ED' }, // orange
  { color: '#DC2626', bg: '#FEF2F2' }, // red
  { color: '#0891B2', bg: '#ECFEFF' }, // cyan
  { color: '#7C3AED', bg: '#F5F3FF' }, // purple
  { color: '#059669', bg: '#ECFDF5' }, // green
  { color: '#9333EA', bg: '#FAF5FF' }, // violet
  { color: '#0E2D43', bg: '#F1F5F9' }, // navy
  { color: '#B45309', bg: '#FFFBEB' }, // amber
];

const ProductCategorySelect = ({ value, onChange, categories, onCreate, dataTestId = 'product-type-select' }) => {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColorIdx, setNewColorIdx] = useState(0);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Saisissez un libelle');
      return;
    }
    setCreating(true);
    try {
      const palette = PRESET_COLORS[newColorIdx];
      const created = await onCreate({
        label,
        color: palette.color,
        bg: palette.bg,
        sort_order: (categories?.length || 0) + 1,
      });
      onChange(created.value);
      toast.success(`Categorie "${created.label}" ajoutee`);
      setAdding(false);
      setNewLabel('');
      setNewColorIdx(0);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Echec de creation';
      toast.error(typeof msg === 'string' ? msg : 'Echec de creation');
    } finally {
      setCreating(false);
    }
  };

  if (adding) {
    return (
      <div className="border rounded p-3" style={{ borderColor: '#0E2D43', backgroundColor: '#F8FAFC' }}>
        <input type="text" placeholder="Nouvelle categorie (ex: Decapant)"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          data-testid="new-category-label-input"
          className="w-full px-3 py-2 border rounded text-sm mb-2"
          style={{ borderColor: '#D1D5DB' }} autoFocus />
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <span className="text-[11px]" style={{ color: '#6B7280' }}>Couleur :</span>
          {PRESET_COLORS.map((p, i) => (
            <button type="button" key={i} onClick={() => setNewColorIdx(i)}
              data-testid={`new-category-color-${i}`}
              className="w-6 h-6 rounded border-2"
              style={{
                backgroundColor: p.bg,
                borderColor: newColorIdx === i ? p.color : 'transparent',
              }}>
              <span className="block w-3 h-3 mx-auto rounded-full" style={{ backgroundColor: p.color }} />
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={handleCreate} disabled={creating}
            data-testid="confirm-new-category-button"
            className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50"
            style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
            <Check size={14} /> Creer
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewLabel(''); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-sm border"
            style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
            <X size={14} /> Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={dataTestId}
        className="flex-1 px-4 py-2 border rounded"
        style={{ borderColor: '#D1D5DB' }}
      >
        <option value="">-- Non classifie --</option>
        {(categories || []).map(c => (
          <option key={c.id || c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      <button type="button" onClick={() => setAdding(true)}
        data-testid="add-category-button"
        title="Ajouter une categorie"
        className="flex items-center gap-1 px-3 py-2 rounded font-medium border"
        style={{ borderColor: '#0E2D43', color: '#0E2D43' }}>
        <Plus size={14} />
      </button>
    </div>
  );
};

export default ProductCategorySelect;
